import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import {
	IMessageContextState,
	messageContext,
} from '../../../message/message.context';
import { BreakLine } from '../../../UI/BreakLine/BreakLine';
import { MessageHandler } from '../../../message/Message';
import { IChatMessages } from '@enuan/chat__utils';
import { configurationContext } from '../../../../contexts/configuration';

/**
 * Wraps a single message block: populates the message context and renders the
 * day-separator before the first message of a new calendar day.
 *
 * @param isFirstBlock Whether this is the first block of a sender group.
 * @param isFirstMessage Whether this is the first message of its block.
 * @param isLastBlock Whether this is the last block of a sender group.
 * @param message The message data to render.
 * @param isDifferentDayBlock Whether this block starts a new calendar day.
 * @param children Extra content injected by the parent renderer (e.g. unread separator).
 * @param isLastMessage Whether this is the last message of its block.
 */
export const MessageWrapper = ({
	isFirstBlock = false,
	isFirstMessage = false,
	isLastBlock = false,
	message,
	isDifferentDayBlock,
	children,
	isLastMessage,
	...props
}: React.PropsWithChildren<IMessageContextState<IChatMessages>>) => {
	const { localization } = useContext(configurationContext);
	const { date: dateFormat, time: timeFormat } =
		localization?.datetimes?.day_separator ?? {};

	const intl = useIntl();

	const showDayLine = isDifferentDayBlock && isFirstMessage;

	return (
		<messageContext.Provider
			value={{
				...props,
				message,
				isFirstBlock,
				isFirstMessage,
				isLastBlock,
				isDifferentDayBlock,
				isLastMessage,
			}}
			key={message.client_id}
		>
			{showDayLine && (
				<BreakLine className="day">
					{intl.formatDate(message.timestamp, {
						dateStyle: dateFormat ?? 'full',
						timeStyle: timeFormat,
					})}
				</BreakLine>
			)}
			{children}
			<MessageHandler />
		</messageContext.Provider>
	);
};
